import { StoreInfo, StoreSettings } from '@/types';
import { HistoryItemProps } from '../components/History';

export type NativeBarcodeEventDetail = {
    value?: string;
    source?: string;
    timestamp?: number;
};
export type ScanHandler = (barcode: string) => void | Promise<void>;
export interface Camera {
    deviceId: string;
    label: string;
    isRearCamera: boolean;
}

export type CameraSystemContextValue = {
    cameras: Camera[];
    defaultCameraId: string | null;
    loading: boolean;
    error: string | null;
    loaded: boolean;
    refreshDevices: () => void;
};

export type TabKey =
    | 'association'
    | 'multi-assoc'
    | 'deassociation'
    | 'out-of-stock'
    | 'led-control'
    | 'register-device';

export type MobileState = {
    activeTab: TabKey;
    storeState: StoreState;
    captureState: CaptureState;
    isSubmitting: boolean;
    esl: string;
    buttonInfo: ButtonInfo;
    historyItems: HistoryItemProps[];
    keyboardEnabled: boolean;
};
export type ButtonInfo = {
    type: 'idle' | 'loading' | 'sending' | 'success' | 'error';
    msg?: string;
};

type StoreState = {
    loading: boolean;
    info: Partial<StoreInfo>;
    settings: Partial<StoreSettings>;
};
type Mode = 'scanner' | 'camera';
type CaptureState = {
    mode: Mode;
    cameraId: string | null;
    cameraSessionKey: number;
};
export type IdentifierInfo = {
    width: number | null;
    height: number | null;
};
export type MobileAction =
    | { type: 'SET_STORE_LOADING'; payload: boolean }
    | { type: 'SET_STORE_DETAILS'; payload: { info: Partial<StoreInfo>; settings: Partial<StoreSettings> } }
    | { type: 'SET_CAPTURE_MODE'; payload: Mode }
    | { type: 'SET_CAMERA_ID'; payload: string }
    | { type: 'SET_ESL'; payload: string }
    | { type: 'SET_KEYBOARD_ENABLED'; payload: boolean }
    | {
          type: 'SET_STATUS_BANNER';
          payload: {
              type: 'idle' | 'loading' | 'success' | 'error';
              message?: string;
          };
      }
    | { type: 'ADD_HISTORY_ITEM'; payload: HistoryItemProps }
    | { type: 'SET_BUTTON_INFO'; payload: ButtonInfo }
    | { type: 'SET_ACTIVE_TAB'; payload: TabKey };

export const defaultState: MobileState = {
    activeTab: 'association',
    storeState: {
        loading: true,
        info: {},
        settings: {},
    },
    captureState: {
        mode: 'scanner',
        cameraId: null,
        cameraSessionKey: 0,
    },
    isSubmitting: false,
    esl: '',
    buttonInfo: {
        type: 'idle',
    },
    historyItems: [],
    keyboardEnabled: false,
};

export const reducer = (state: MobileState, action: MobileAction) => {
    switch (action.type) {
        case 'SET_STORE_LOADING':
            return {
                ...state,
                storeState: {
                    ...state.storeState,
                    loading: action.payload,
                },
            };
        case 'SET_STORE_DETAILS': {
            const { info, settings } = action.payload;
            return {
                ...state,
                storeState: {
                    ...state.storeState,
                    info,
                    settings,
                },
            };
        }
        case 'SET_CAPTURE_MODE': {
            return {
                ...state,
                captureState: {
                    ...state.captureState,
                    mode: action.payload,
                },
            };
        }
        case 'SET_CAMERA_ID': {
            return {
                ...state,
                captureState: {
                    ...state.captureState,
                    cameraId: action.payload,
                },
            };
        }
        case 'SET_ESL':
            return {
                ...state,
                esl: action.payload,
            };
        case 'SET_KEYBOARD_ENABLED':
            return {
                ...state,
                keyboardEnabled: action.payload,
            };
        case 'SET_STATUS_BANNER':
            return {
                ...state,
                statusBanner: action.payload,
            };
        case 'ADD_HISTORY_ITEM':
            return {
                ...state,
                historyItems: [action.payload, ...state.historyItems],
            };
        case 'SET_BUTTON_INFO':
            return {
                ...state,
                isSubmitting: action.payload.type === 'sending',
                buttonInfo: action.payload,
            };
        case 'SET_ACTIVE_TAB':
            return {
                ...state,
                activeTab: action.payload,
            };
        default:
            return state;
    }
};
